/**
 * Empty-state primitives — one visual language across the app.
 *
 * Use `EmptyState` with a `layout`:
 *   - `pane` — full remaining flex area, vertically centered (Automations,
 *     Integrations, Tables overview).
 *   - `inline` — centered block inside a parent that already handles
 *     placement (welcome screen, dialog bodies).
 *   - `inset` — compact, left-aligned for sidebar sections (DM list).
 *
 * Legacy names (`EmptyStatePresentational`, `EmptyStateFullPane`, …) are
 * thin aliases kept for gradual migration.
 */
import type { ComponentType, ReactNode } from "react";
import type { IconProps } from "@solar-icons/react";

import { Warning } from "@/components/ui/icons";
import {
  EMPTY_STATE_PANE_CENTER,
  PANEL_SURFACE_CLASSES,
  TEXT_DESCRIPTION,
} from "@/lib/surface-styles";
import { cn } from "@/lib/utils";

type SolarIcon = ComponentType<IconProps>;

export type EmptyStateLayout = "pane" | "inline" | "inset";
export type EmptyStateTone = "default" | "positive";

export type EmptyStateProps = {
  icon?: SolarIcon;
  iconWeight?: IconProps["weight"];
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  layout?: EmptyStateLayout;
  tone?: EmptyStateTone;
  className?: string;
};

function EmptyStateContent({
  icon: Icon,
  iconWeight = "Linear",
  title,
  description,
  action,
  layout,
  tone,
  className,
}: EmptyStateProps) {
  const isInset = layout === "inset";

  return (
    <div
      className={cn(
        "flex flex-col gap-3",
        isInset
          ? "items-start gap-2 px-2 py-2 text-left"
          : "items-center px-6 py-12 text-center",
        className,
      )}
    >
      {Icon && (
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full bg-foreground/[0.06] text-foreground/70",
            isInset ? "h-8 w-8" : "h-11 w-11",
          )}
        >
          <Icon size={isInset ? 16 : 20} weight={iconWeight} />
        </div>
      )}
      <div className={cn(isInset ? "min-w-0 w-full" : "space-y-1")}>
        {isInset && !description ? (
          <p className="text-xs font-normal leading-snug text-muted-foreground">
            {title}
          </p>
        ) : (
          <>
            <h3
              className={cn(
                "font-medium text-foreground",
                isInset ? "text-sm" : "text-base",
              )}
            >
              {title}
            </h3>
            {description && (
              <p
                className={cn(
                  TEXT_DESCRIPTION,
                  !isInset && "mx-auto max-w-sm",
                  isInset && "text-xs leading-snug",
                  tone === "positive" && "text-muted-foreground/70",
                )}
              >
                {description}
              </p>
            )}
          </>
        )}
      </div>
      {action && (
        <div className={cn(isInset ? "w-full" : "pt-1")}>{action}</div>
      )}
    </div>
  );
}

/** Canonical empty state — prefer this for new code. */
export function EmptyState({
  layout = "inline",
  className,
  ...props
}: EmptyStateProps) {
  const content = (
    <EmptyStateContent layout={layout} className={className} {...props} />
  );

  if (layout === "pane") {
    return <div className={EMPTY_STATE_PANE_CENTER}>{content}</div>;
  }

  return content;
}

/** @deprecated Use `<EmptyState layout="inline" />` */
export function EmptyStatePresentational(
  props: Omit<EmptyStateProps, "layout">,
) {
  return <EmptyState layout="inline" {...props} />;
}

/** @deprecated Use `<EmptyState layout="pane" />` */
export function EmptyStateFullPane(
  props: Omit<EmptyStateProps, "layout"> & { icon: SolarIcon },
) {
  return <EmptyState layout="pane" {...props} />;
}

/** @deprecated Use `<EmptyState layout="pane" tone="positive" />` */
export function EmptyStateCaughtUp(
  props: Omit<EmptyStateProps, "layout" | "tone">,
) {
  return <EmptyState layout="pane" tone="positive" {...props} />;
}

export function MissingRouteAdmonition({
  title,
  description,
  action,
  className,
}: {
  title: string;
  description: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("max-w-md p-4", PANEL_SURFACE_CLASSES, className)}>
      <div className="flex gap-3">
        <div className="mt-0.5 shrink-0 text-muted-foreground">
          <Warning size={18} />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className={TEXT_DESCRIPTION}>{description}</p>
          {action && <div className="pt-2">{action}</div>}
        </div>
      </div>
    </div>
  );
}

/**
 * Single explanatory row for use inside a populated list/table layout.
 * Pass `colSpan` matching the table's column count.
 */
export function EmptyStateRow({
  colSpan,
  title,
  description,
  className,
}: {
  colSpan: number;
  title: string;
  description?: ReactNode;
  className?: string;
}) {
  return (
    <td colSpan={colSpan} className={cn("px-4 py-6", className)}>
      <p className="text-sm font-normal text-foreground">{title}</p>
      {description && <p className={TEXT_DESCRIPTION}>{description}</p>}
    </td>
  );
}
